import React, { useMemo } from 'react';
import { faCar, faFileText, faWrench } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  AssignmentModel,
  AssignmentViewModel,
  CaseModel,
  CaseTypeEnum,
  GlobalSettingEnum,
} from 'api/model';
import styled from 'styled-components';
import { useGlobalSettingsGetSettings } from 'api/global-settings/global-settings';

const IconGroup = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

export type WarningInfo = {
  warning: string;
  icon?: React.ReactNode;
  details?: string;
};

export default function useAssignmentWarnings() {
  const { data: settings, isLoading } = useGlobalSettingsGetSettings({
    query: { staleTime: 5 * 60 * 1000 },
  });

  const values = useMemo(() => {
    return {
      highMileage: Number(
        settings?.[GlobalSettingEnum.MinMileageToFlagAsRiskVehicle] ?? 14000
      ),
      highAge: Number(
        settings?.[GlobalSettingEnum.MinAgeToFlagAsRiskVehicle] ?? 10
      ),
    };
  }, [settings]);

  const getAssignmentWarnings = (
    assignment: AssignmentModel
  ): WarningInfo[] => {
    const warnings: WarningInfo[] = [];
    if (assignment.hasUncheckedFlaggedForSpeeding) {
      warnings.push({
        warning: 'Uppdraget har hastighetsöverträdelser',
      });
    }
    if (assignment.hasUncheckedDeviationReport) {
      warnings.push({
        warning: 'Uppdraget har en ohanterad avvikelse-rapport',
        icon: <FontAwesomeIcon icon={faFileText} />,
      });
    }
    if (assignment.assigneeReportedMissingGear) {
      warnings.push({
        warning: 'Fälttestaren saknade arbetsutrustning vid start',
      });
    }
    if (assignment.inspectionApproved === false) {
      warnings.push({
        warning: 'Besiktningen är inte godkänd',
      });
    }
    if (assignment.assignmentVehicleFaultDiscovered) {
      warnings.push({
        warning: 'Fel på fordonet upptäckt',
        icon: (
          <IconGroup>
            <FontAwesomeIcon icon={faWrench} size="2xs" />
            <FontAwesomeIcon icon={faCar} />
          </IconGroup>
        ),
      });
    }
    return warnings;
  };

  const getInspectionNotApprovedWarning = (
    assignment: AssignmentModel
  ): WarningInfo | null => {
    if (assignment.inspectionApproved === false) {
      return {
        warning: 'Besiktningen är inte godkänd',
      };
    }
    return null;
  };

  const getVehicleFaultWarning = (
    assignment: AssignmentModel
  ): WarningInfo | null => {
    if (assignment.assignmentVehicleFaultDiscovered) {
      return {
        warning: 'Fel på fordonet upptäckt',
        icon: (
          <IconGroup>
            <FontAwesomeIcon icon={faWrench} size="2xs" />
            <FontAwesomeIcon icon={faCar} />
          </IconGroup>
        ),
      };
    }
    return null;
  };

  const getCaseWarnings = (model: CaseModel): WarningInfo[] => {
    const warnings: WarningInfo[] = [];
    // use dynamic setting for high mileage
    let highMileage: boolean = false;
    let highAge: boolean = false;
    if (model.vehicleMileage > values.highMileage) {
      highMileage = true;
      warnings.push({
        warning: 'Högmilare',
      });
    }
    // check year model age with dynamic setting
    const currentYear = new Date().getFullYear();
    const { vehicleYearModel } = model;
    if (vehicleYearModel) {
      try {
        const yearModel = parseInt(vehicleYearModel, 10);
        const age = currentYear - yearModel;
        if (age > values.highAge) {
          highAge = true;
          warnings.push({
            warning: 'Fordonet är av äldre modell',
          });
        }
      } catch (error) {
        // ...error handling...
      }
    }

    if (
      highMileage &&
      highAge &&
      (model.caseTypeID === CaseTypeEnum.Purchase ||
        model.caseTypeID === CaseTypeEnum.Auction)
    ) {
      warnings.push({
        warning: 'RISKBIL',
      });
    }

    return warnings;
  };

  const getCaseAndAssignmentWarnings = (model: AssignmentViewModel) => {
    const warnings = getAssignmentWarnings(model.assignment);
    const caseWarnings = getCaseWarnings(model.case);
    return [...warnings, ...caseWarnings];
  };

  return {
    isLoading,
    getAssignmentWarnings,
    getCaseWarnings,
    getCaseAndAssignmentWarnings,
    getInspectionNotApprovedWarning,
    getVehicleFaultWarning,
  };
}
